import {verifiedFetchJSON} from '@github-ui/verified-fetch'
import type {Label} from './label'
import type {GraphQLResponse} from '@github-ui/item-picker-base/api/graphql'
import {buildItemPickerSearchUrl} from '@github-ui/item-picker-base/api/urls'

/**
 * The types here align with the query made on the server defined here:
 * https://github.com/github/github/blob/master/app/controllers/ghui/item_pickers/labels_controller.rb
 * If the shape of that response changes, this type should be
 * updated to reflect any modified fields
 */

export type GetSearchLabelsRequest = {
  owner: string
  repo: string
  query?: string
  count?: number
  labelNames?: string[]
}

type GetSearchLabelsResponseData = {
  repository: {
    id: string
    labels: {
      nodes: Label[]
      totalCount: number
    }
    labelsByNames?: {
      nodes: Label[]
    }
  }
}

export type GetSearchLabelsResponse = GraphQLResponse<GetSearchLabelsResponseData>

export async function apiGetSearchLabels({
  owner,
  repo,
  query,
  count,
  labelNames,
}: GetSearchLabelsRequest): Promise<GetSearchLabelsResponse> {
  const params: Record<string, unknown> = {owner, repo}

  if (query) {
    params['query'] = query
  }

  if (count) {
    params['count'] = count
  }

  if (labelNames && labelNames.length > 0) {
    params['labelNames'] = labelNames.join(',')
  }

  const requestUrl = buildItemPickerSearchUrl('labels', params)
  const request = await verifiedFetchJSON(requestUrl)
  return (await request.json()) as GetSearchLabelsResponse
}

const itemPickerQueryKeyBase = 'item_picker'

export function buildItemPickerLabelSearchQueryKey(searchParams: GetSearchLabelsRequest) {
  return [itemPickerQueryKeyBase, 'labels', searchParams]
}

export type LabelPickerQueryData = {
  repositoryId: string
  labels: Label[]
  totalCount: number
  labelsByNames?: Label[]
}

/**
 * Helper function to strip quotes from label names and optionally filter labels by those names
 * @param labelNames Array of label names that may have quotes
 * @param labels Optional array of labels to filter
 * @returns Object with stripped names and optionally filtered labels
 */
export function processLabelNames(labelNames: string[], labels: Label[]) {
  const strippedNames = labelNames.map(name => name.replace(/^"/, '').replace(/"$/, ''))

  return labels.filter(label => strippedNames.includes(label.name))
}

export function transformSearchLabelResponseToQueryData(
  response: GetSearchLabelsResponseData,
  labelNames?: string[],
): LabelPickerQueryData {
  let matchedLabels: Label[] | undefined = response.repository.labelsByNames?.nodes

  // If labelNames were provided, filter to only those that match after stripping quotes
  if (labelNames && labelNames.length > 0 && matchedLabels) {
    matchedLabels = processLabelNames(labelNames, matchedLabels)
  }

  return {
    labels: response.repository.labels.nodes,
    repositoryId: response.repository.id,
    totalCount: response.repository.labels.totalCount,
    labelsByNames: matchedLabels,
  }
}
